import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { FreezerRoom } from './scenes/FreezerRoom';
import { Freezer } from './scenes/Freezer';
import { Shelf } from './scenes/Shelf';
import { SampleData } from './scenes/SampleBox';
import { SearchPanel } from './ui/SearchPanel';
import { updateTweens } from './utils/animate';
import { tween, Easing } from './utils/animate';
import mockData from './data/mockData.json';

// ─── Renderer ───
const container = document.getElementById('canvas-container')!;
const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.6;
container.appendChild(renderer.domElement);

// ─── Scene ───
const scene = new THREE.Scene();
scene.background = new THREE.Color(0xdce3eb);
scene.fog = new THREE.Fog(0xdce3eb, 8, 18);

// ─── Camera ───
const camera = new THREE.PerspectiveCamera(50, window.innerWidth / window.innerHeight, 0.01, 50);
camera.position.set(0, 2.5, 5);
camera.lookAt(0, 0.6, 0);

// ─── Controls ───
const controls = new OrbitControls(camera, renderer.domElement);
controls.enableDamping = true;
controls.dampingFactor = 0.08;
controls.minDistance = 0.5;
controls.maxDistance = 10;
controls.maxPolarAngle = Math.PI / 2 + 0.1;
controls.target.set(0, 0.6, 0);
controls.update();

// ─── Build the room and freezers ───
const room = new FreezerRoom(scene);
room.loadFreezers(mockData.freezers);

// ─── Search Panel ───
const searchPanel = new SearchPanel(room);

// Keresési találat kezelés
searchPanel.onResults(async (results) => {
  // Alaphelyzetbe állítás: bezárjuk amit nyitva volt
  if (activeShelf) {
    await activeShelf.retract();
    activeShelf = null;
  }
  if (activeFreezer) {
    await activeFreezer.close();
    activeFreezer = null;
  }
  // Az összes nyitott hűtőt bezárjuk
  for (const f of room.freezers) {
    if (f.isOpen) await f.close();
  }

  if (results.length === 0) {
    // Nincs találat vagy reset → vissza az eredeti kamerához
    animateCameraTo(defaultCameraPos.clone(), defaultCameraTarget.clone());
    updateBreadcrumb();
    return;
  }

  // Találati hűtők kinyitása
  const matchingFreezerIds = [...new Set(results.map(r => r.freezerId))];
  for (const fid of matchingFreezerIds) {
    const freezer = room.getFreezerById(fid);
    if (freezer && !freezer.isOpen) {
      await freezer.open();
    }
  }

  // Kamera: kicsit hátrébb húzódik, hogy az összképet lássa
  animateCameraTo(
    new THREE.Vector3(0, 2.2, 4.5),
    new THREE.Vector3(0, 0.6, -1)
  );

  updateBreadcrumb();
});

// ─── State ───
let activeFreezer: Freezer | null = null;
let activeShelf: Shelf | null = null;
const defaultCameraPos = new THREE.Vector3(0, 2.5, 5);
const defaultCameraTarget = new THREE.Vector3(0, 0.6, 0);
const savedCameraState = { position: defaultCameraPos.clone(), target: defaultCameraTarget.clone() };

// ─── Raycaster ───
const raycaster = new THREE.Raycaster();
const mouse = new THREE.Vector2();

// Click vs drag megkülönböztetés
let mouseDownPos = { x: 0, y: 0 };
let mouseDownTime = 0;
const CLICK_THRESHOLD = 5; // px

window.addEventListener('mousedown', (e) => {
  if (e.button === 0) {
    mouseDownPos = { x: e.clientX, y: e.clientY };
    mouseDownTime = performance.now();
  }
});

function onCanvasClick(event: MouseEvent): void {
  // Drag volt, nem kattintás → ne csináljunk semmit
  const dx = event.clientX - mouseDownPos.x;
  const dy = event.clientY - mouseDownPos.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist > CLICK_THRESHOLD) return;

  // Ignore clicks on UI panels
  const target = event.target as HTMLElement;
  if (target.closest('#search-panel') || target.closest('#detail-panel') || target.closest('#breadcrumb')) {
    return;
  }

  mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
  mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

  raycaster.setFromCamera(mouse, camera);
  const intersects = raycaster.intersectObjects(scene.children, true);

  // Összegyűjtjük az összes találatot típus szerint, prioritás: sample > box > shelf > freezer
  let hitSample: SampleData | null = null;
  let hitShelf: Shelf | null = null;
  let hitFreezer: Freezer | null = null;
  let hitBox = false;
  let hitBoxParentShelf: Shelf | null = null;

  for (const hit of intersects) {
    const obj = findUserData(hit.object);
    if (!obj) continue;
    const ud = obj.userData;

    if (!hitSample && ud.type === 'sample' && ud.sampleData) {
      hitSample = ud.sampleData as SampleData;
    }
    if (!hitBox && ud.type === 'box' && ud.boxRef) {
      hitBox = true;
      let parent = obj.parent;
      while (parent) {
        if (parent.userData?.type === 'shelf' && parent.userData.shelfRef) {
          hitBoxParentShelf = parent.userData.shelfRef as Shelf;
          break;
        }
        parent = parent.parent;
      }
    }
    if (!hitShelf && ud.type === 'shelf' && ud.shelfRef) {
      hitShelf = ud.shelfRef as Shelf;
    }
    if (!hitFreezer && ud.type === 'freezer' && ud.freezerRef) {
      hitFreezer = ud.freezerRef as Freezer;
    }
  }

  // Ha van találat hűtőn belül (minta/doboz/polc), de a hűtő csukva van → nyissuk ki
  if ((hitSample || hitBox || hitShelf) && hitFreezer && !hitFreezer.isOpen) {
    handleFreezerClick(hitFreezer);
    return;
  }

  // Prioritásos feldolgozás - csak nyitott hűtőnél
  if (hitSample && hitFreezer?.isOpen) {
    handleSampleClick(hitSample);
    return;
  }
  if (hitBox && hitBoxParentShelf && hitFreezer?.isOpen) {
    handleShelfClick(hitBoxParentShelf);
    return;
  }
  if (hitShelf && hitFreezer?.isOpen) {
    handleShelfClick(hitShelf);
    return;
  }
  if (hitFreezer) {
    handleFreezerClick(hitFreezer);
    return;
  }

  // Üres területre kattintás → visszalépés
  if (!hitFreezer && !hitShelf && !hitSample && !hitBox) {
    goBack();
  }
}

function findUserData(obj: THREE.Object3D): THREE.Object3D | null {
  let current: THREE.Object3D | null = obj;
  while (current) {
    if (current.userData?.type) return current;
    current = current.parent;
  }
  return null;
}

// Visszalépés: polc → hűtő → szoba
async function goBack(): Promise<void> {
  if (activeShelf) {
    await activeShelf.retract();
    activeShelf = null;
    updateBreadcrumb();
    // Kamera vissza a hűtő nézethez
    if (activeFreezer) {
      const freezerPos = new THREE.Vector3();
      activeFreezer.mesh.getWorldPosition(freezerPos);
      animateCameraTo(
        new THREE.Vector3(freezerPos.x + 0.3, freezerPos.y + 0.7, freezerPos.z + 1.8),
        new THREE.Vector3(freezerPos.x, freezerPos.y + 0.6, freezerPos.z)
      );
    }
    return;
  }
  if (activeFreezer) {
    await activeFreezer.close();
    activeFreezer = null;
    searchPanel.hideDetail();
    animateCameraTo(defaultCameraPos.clone(), defaultCameraTarget.clone());
    updateBreadcrumb();
    return;
  }
}

async function handleFreezerClick(freezer: Freezer): Promise<void> {
  // Ha ez a hűtő már nyitva van, ne csináljon semmit (bezárás csak breadcrumbon)
  if (activeFreezer === freezer) {
    return;
  }

  // Close previous
  if (activeFreezer) {
    await activeFreezer.close();
  }

  // Save camera
  savedCameraState.position.copy(camera.position);
  savedCameraState.target.copy(controls.target);

  activeFreezer = freezer;
  activeShelf = null;

  // Open door
  await freezer.open();

  // Zoom camera to freezer
  const freezerPos = new THREE.Vector3();
  freezer.mesh.getWorldPosition(freezerPos);
  const targetPos = new THREE.Vector3(
    freezerPos.x + 0.3,
    freezerPos.y + 0.7,
    freezerPos.z + 1.8
  );
  const targetLookAt = new THREE.Vector3(freezerPos.x, freezerPos.y + 0.6, freezerPos.z);
  animateCameraTo(targetPos, targetLookAt);
  updateBreadcrumb();
}

async function handleShelfClick(shelf: Shelf): Promise<void> {
  if (activeShelf === shelf) {
    await shelf.retract();
    activeShelf = null;
    updateBreadcrumb();
    return;
  }

  // Retract previous
  if (activeShelf) {
    await activeShelf.retract();
  }

  activeShelf = shelf;
  await shelf.extend();
  updateBreadcrumb();

  // Move camera closer to shelf
  if (activeFreezer) {
    const freezerPos = new THREE.Vector3();
    activeFreezer.mesh.getWorldPosition(freezerPos);
    const shelfWorldY = shelf.mesh.position.y + freezerPos.y;
    const targetPos = new THREE.Vector3(
      freezerPos.x + 0.2,
      shelfWorldY + 0.3,
      freezerPos.z + 1.3
    );
    const targetLookAt = new THREE.Vector3(freezerPos.x, shelfWorldY + 0.05, freezerPos.z);
    animateCameraTo(targetPos, targetLookAt);
  }
}

function handleSampleClick(sample: SampleData): void {
  // Find which freezer this sample belongs to
  for (const freezer of room.freezers) {
    for (const shelf of freezer.shelves) {
      for (const box of shelf.boxes) {
        if (box.sampleMeshes.has(sample.id)) {
          searchPanel.showDetail(sample, freezer);
          return;
        }
      }
    }
  }
}

function animateCameraTo(targetPosition: THREE.Vector3, targetLookAt: THREE.Vector3): void {
  const startPos = camera.position.clone();
  const startTarget = controls.target.clone();

  tween({
    from: 0,
    to: 1,
    duration: 1000,
    easing: Easing.easeInOutCubic,
    onUpdate: (t) => {
      camera.position.lerpVectors(startPos, targetPosition, t);
      controls.target.lerpVectors(startTarget, targetLookAt, t);
      controls.update();
    },
  });
}

// ─── Breadcrumb ───
function updateBreadcrumb(): void {
  const el = document.getElementById('breadcrumb')!;
  if (!activeFreezer) {
    el.style.display = 'none';
    return;
  }

  el.style.display = 'block';
  let html = `<span id="bc-room">Szoba</span>`;
  html += ` › <span id="bc-freezer">${activeFreezer.freezerData.label}</span>`;
  if (activeShelf) {
    html += ` › <span id="bc-shelf">${activeShelf.shelfData.label}</span>`;
  }
  el.innerHTML = html;

  document.getElementById('bc-room')?.addEventListener('click', () => {
    if (activeFreezer) {
      activeFreezer.close();
      activeFreezer = null;
      activeShelf = null;
      animateCameraTo(savedCameraState.position, savedCameraState.target);
      updateBreadcrumb();
    }
  });

  document.getElementById('bc-freezer')?.addEventListener('click', () => {
    if (activeShelf) {
      activeShelf.retract();
      activeShelf = null;
      updateBreadcrumb();
      if (activeFreezer) {
        const freezerPos = new THREE.Vector3();
        activeFreezer.mesh.getWorldPosition(freezerPos);
        animateCameraTo(
          new THREE.Vector3(freezerPos.x + 0.3, freezerPos.y + 0.7, freezerPos.z + 1.8),
          new THREE.Vector3(freezerPos.x, freezerPos.y + 0.6, freezerPos.z)
        );
      }
    }
  });
}

// ─── Resize ───
function onResize(): void {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight);
}

// ─── Render loop ───
const clock = new THREE.Clock();

function animate(): void {
  requestAnimationFrame(animate);

  const delta = clock.getDelta();
  const elapsed = clock.getElapsedTime();

  updateTweens();
  controls.update();
  room.update(elapsed, delta);

  renderer.render(scene, camera);
}

// ─── Gyors navigáció mintához ───
async function navigateToSample(sampleId: string): Promise<void> {
  const id = sampleId.trim().toUpperCase();
  if (!id) return;

  // Megkeressük melyik hűtőben, polcon, dobozban van
  let targetFreezer: Freezer | null = null;
  let targetShelf: Shelf | null = null;
  let targetSample: SampleData | null = null;

  for (const freezer of room.freezers) {
    for (const shelf of freezer.shelves) {
      for (const box of shelf.boxes) {
        for (const [sid, mesh] of box.sampleMeshes) {
          if (sid.toUpperCase() === id) {
            targetFreezer = freezer;
            targetShelf = shelf;
            targetSample = mesh.userData.sampleData as SampleData;
            break;
          }
        }
        if (targetSample) break;
      }
      if (targetSample) break;
    }
    if (targetSample) break;
  }

  if (!targetFreezer || !targetShelf || !targetSample) {
    alert(`Minta nem található: ${sampleId}`);
    return;
  }

  // Előző állapot bezárása
  if (activeShelf) {
    await activeShelf.retract();
    activeShelf = null;
  }
  if (activeFreezer && activeFreezer !== targetFreezer) {
    await activeFreezer.close();
    activeFreezer = null;
  }

  // Hűtő kinyitása
  if (!targetFreezer.isOpen) {
    activeFreezer = targetFreezer;
    await targetFreezer.open();
  } else {
    activeFreezer = targetFreezer;
  }

  // Kamera a hűtőhöz
  const freezerPos = new THREE.Vector3();
  targetFreezer.mesh.getWorldPosition(freezerPos);
  animateCameraTo(
    new THREE.Vector3(freezerPos.x + 0.3, freezerPos.y + 0.7, freezerPos.z + 1.8),
    new THREE.Vector3(freezerPos.x, freezerPos.y + 0.6, freezerPos.z)
  );

  // Kis várakozás hogy a kamera animáció elinduljon
  await new Promise(r => setTimeout(r, 400));

  // Polc kihúzása
  activeShelf = targetShelf;
  await targetShelf.extend();

  // Kamera közelebb a polchoz
  const shelfWorldY = targetShelf.mesh.position.y + freezerPos.y;
  animateCameraTo(
    new THREE.Vector3(freezerPos.x + 0.2, shelfWorldY + 0.3, freezerPos.z + 1.3),
    new THREE.Vector3(freezerPos.x, shelfWorldY + 0.05, freezerPos.z)
  );

  // Minta kiemelése
  for (const shelf of targetFreezer.shelves) {
    for (const box of shelf.boxes) {
      box.unhighlightAll();
      if (box.sampleMeshes.has(targetSample.id)) {
        box.highlightSample(targetSample.id);
      }
    }
  }

  // Részletek panel megjelenítése
  searchPanel.showDetail(targetSample, targetFreezer);
  updateBreadcrumb();
}

document.getElementById('quick-navigate-btn')!.addEventListener('click', () => {
  const input = document.getElementById('quick-sample-id') as HTMLInputElement;
  navigateToSample(input.value);
});
document.getElementById('quick-sample-id')!.addEventListener('keydown', (e) => {
  if (e.key === 'Enter') {
    navigateToSample((e.target as HTMLInputElement).value);
  }
});

// ─── Init ───
window.addEventListener('resize', onResize);
window.addEventListener('click', onCanvasClick);

// Escape / jobb klikk → visszalépés
window.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') goBack();
});
window.addEventListener('contextmenu', (e) => {
  if (activeFreezer) {
    e.preventDefault();
    goBack();
  }
});

// Cursor change on hover
window.addEventListener('mousemove', (event) => {
  mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
  mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
  raycaster.setFromCamera(mouse, camera);
  const intersects = raycaster.intersectObjects(scene.children, true);
  let isHovering = false;
  for (const hit of intersects) {
    const obj = findUserData(hit.object);
    if (obj?.userData?.type) {
      isHovering = true;
      break;
    }
  }
  renderer.domElement.style.cursor = isHovering ? 'pointer' : 'default';
});

// Hide loading screen
const loadingEl = document.getElementById('loading');
if (loadingEl) {
  loadingEl.style.opacity = '0';
  setTimeout(() => loadingEl.remove(), 500);
}

// Start
animate();
